import { useState } from "react";
import { BaseWidgetProps, WidgetWrapper } from "shared-ui";
import { MenuItem, Select } from "@mui/material";
import AssetsSpendLineChart from "./spend_components/AssetsSpendLineChart";
import AssetsSpendByCategory from "./spend_components/AssetsSpendByCategory";
import AssetsSpendByManufacturer from "./spend_components/AssetsSpendByManufacturer";
import AssetsSpendByType from "./spend_components/AssetsSpendByType";
import { useSearchParams } from "react-router-dom";

const spendTermMonthsParam = "spendTermMonths";
const validSpendTerms = new Set([6, 12, 24, 36]);

const parseSpendTermMonths = (value: string | null): number | null => {
  if (!value) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || !validSpendTerms.has(parsed)) {
    return null;
  }

  return parsed;
};

export function formatShortNumber(num: number) {
  const absNum = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  if (absNum >= 1_000_000_000)
    return `${sign}${(absNum / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
  if (absNum >= 1_000_000)
    return `${sign}${(absNum / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (absNum >= 1_000) return `${sign}${(absNum / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return num.toString();
}

export default function AssetsExpiringContractsWidget({
  pageLayoutWidget,
}: Readonly<BaseWidgetProps>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTermMonths = parseSpendTermMonths(
    searchParams.get(spendTermMonthsParam),
  );
  const [termMonths, setTermMonths] = useState(initialTermMonths ?? 24);

  const termMonthsOptions = [
    { label: "Last 6 Months", value: 6 },
    { label: "Last 12 Months", value: 12 },
    { label: "Last 24 Months", value: 24 },
    { label: "Last 36 Months", value: 36 },
  ];

  const headerAction = () => (
    <Select
      sx={{ height: "40px" }}
      labelId="TermSelector"
      id="TermSelector"
      value={termMonths}
      onChange={(event) => {
        const selectedValue = Number(event.target.value)
          ? Number(event.target.value)
          : 24;

        setTermMonths(selectedValue);

        const updatedSearchParams = new URLSearchParams(searchParams);
        updatedSearchParams.set(spendTermMonthsParam, `${selectedValue}`);
        setSearchParams(updatedSearchParams, { replace: true });
      }}
      autoWidth
      displayEmpty
    >
      {termMonthsOptions.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
  return (
    <WidgetWrapper
      pageLayoutWidget={pageLayoutWidget}
      cardHeaderDivider
      noPadding={true}
      headerAction={headerAction()}
    >
      <AssetsSpendLineChart termMonths={termMonths} />
      <AssetsSpendByCategory termMonths={termMonths} />
      <AssetsSpendByManufacturer termMonths={termMonths} />
      <AssetsSpendByType />
    </WidgetWrapper>
  );
}
